import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/equipments`;

/**
 * Equipment Service
 * Gerencia todas as operações de equipamentos
 */
class EquipmentService {
  /**
   * Get all equipments
   * @returns {Promise<Array>}
   */
  async getAllEquipments() {
    try {
      const response = await api.get(API_BASE);
      return unwrapList(response);
    } catch (error) {
      // Se for 404, retorna array vazio ao invés de lançar erro
      if (error.response?.status === 404) {
        console.log("ℹ️ Nenhum equipamento encontrado (404)");
        return [];
      }
      console.error("❌ Erro ao buscar equipamentos:", error);
      throw error;
    }
  }

  /**
   * Get equipment by ID
   * @param {number} id - Equipment ID
   * @returns {Promise<object>}
   */
  async getEquipmentById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar equipamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new equipment
   * @param {object} equipmentData - Equipment data
   * @returns {Promise<object>}
   */
  async createEquipment(equipmentData) {
    try {
      this.validateEquipmentData(equipmentData);

      const response = await api.post(
        API_BASE,
        equipmentData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar equipamento:", error);
      throw error;
    }
  }

  /**
   * Update equipment
   * @param {number} id - Equipment ID
   * @param {object} equipmentData - Updated equipment data
   * @returns {Promise<object>}
   */
  async updateEquipment(id, equipmentData) {
    try {
      this.validateEquipmentData(equipmentData);

      const response = await api.put(
        `${API_BASE}/${id}`,
        equipmentData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar equipamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete equipment
   * @param {number} id - Equipment ID
   * @returns {Promise<void>}
   */
  async deleteEquipment(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar equipamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate equipment data
   * @param {object} data - Equipment data to validate
   * @throws {Error} If validation fails
   */
  validateEquipmentData(data) {
    const errors = [];

    if (!data.name || data.name.trim() === "") {
      errors.push("Nome é obrigatório");
    }

    if (!data.type) {
      errors.push("Tipo é obrigatório");
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Get available equipment types
   * @returns {Array<object>}
   */
  getEquipmentTypes() {
    return [
      { value: "FREEZER", label: "Freezer", icon: "❄️" },
      { value: "REFRIGERATOR", label: "Geladeira", icon: "🧊" },
      { value: "DISPLAY_CASE", label: "Vitrine", icon: "🪟" },
      { value: "OVEN", label: "Forno", icon: "🔥" },
      { value: "STOVE", label: "Fogão", icon: "🍳" },
      { value: "MICROWAVE", label: "Micro-ondas", icon: "📻" },
      { value: "MIXER", label: "Batedeira", icon: "🥄" },
      { value: "BLENDER", label: "Liquidificador", icon: "🌪️" },
      { value: "FOOD_PROCESSOR", label: "Processador", icon: "⚙️" },
      { value: "SCALE", label: "Balança", icon: "⚖️" },
      { value: "DISHWASHER", label: "Lava-louças", icon: "🫧" },
      { value: "OTHER", label: "Outro", icon: "🔧" },
    ];
  }

  /**
   * Get equipment type label
   * @param {string} type - Equipment type value
   * @returns {string}
   */
  getEquipmentTypeLabel(type) {
    const types = this.getEquipmentTypes();
    const found = types.find((t) => t.value === type);
    return found ? `${found.icon} ${found.label}` : type;
  }

  /**
   * Get equipment status options
   * @returns {Array<object>}
   */
  getStatusOptions() {
    return [
      { value: "ACTIVE", label: "Ativo", color: "green" },
      { value: "MAINTENANCE", label: "Manutenção", color: "yellow" },
      { value: "INACTIVE", label: "Inativo", color: "red" },
    ];
  }

  /**
   * Get status label and color
   * @param {string} status - Equipment status
   * @returns {object}
   */
  getStatusInfo(status) {
    const statuses = this.getStatusOptions();
    const found = statuses.find((s) => s.value === status);
    return found || { label: status, color: "gray" };
  }
}

export default new EquipmentService();
